import { CONDITIONAL_STATES, CONDITIONAL_TRIGGERS } from '@app/constants';
import { Conditional } from '@app/models/form.model';
import { KeyValue } from '@app/services/webform.service';
import { saveAs } from 'file-saver';
import numeral from 'numeral';

/**
 * Evaluates a template string containing conditional statements and returns the result.
 *
 * @param {string} value - The template string to evaluate.
 * @param {object} data - The data object containing properties referenced in the template string.
 * @returns {string} The evaluated result of the template string.
 */
export function evaluateWebformConfirmatioTemplate(value: string): string {
  // Regular expression pattern to match the template conditional statements.
  const pattern =
    /{{\s*IF\s+([^}]+)}}([\s\S]*?){{\s*THEN\s+([\s\S]+?)}}([\s\S]*?)({{\s*ELSE\s+([\s\S]+?)}}([\s\S]*?))?{{\s*ENDIF\s*}}/;
  let result = value;
  const matches = value.match(pattern);

  if (matches) {
    // Extract the condition, then-clause, and else-clause from the matched template string.
    const condition = matches[1];
    const thenClause = matches[3];
    const elseClause = matches[6];
    const statement = `${condition} ? \`${thenClause}\` : \`${elseClause || ''}\``;
    // @ts-ignore
    result = new Function(`"use strict"; return (${statement})`)();
    // result = eval(statement);
  }

  return result;
}

export function replaceFieldPlaceholders(
  value: string,
  formData: KeyValue,
  optionsElements: KeyValue,
  formId: string
) {
  return value.replace(/\[(.+?)\]/g, (match: string, token: string) => {
    const parts: string[] = token.split(':');
    const controlId: string = `${formId}--${parts[0]}`;
    let replace: string = formData[controlId];

    // get given property of the data the property
    // of the field can be specified like this:
    // [field_name:property]
    if (optionsElements && controlId && parts[1]) {
      const option = optionsElements[controlId]?.filter(
        (opt: KeyValue) => opt.key === replace
      );
      if (option?.length) replace = option[0][parts[1]];
    }
    return replace || '';
  });
}
/**
 * Flatten the form group data to a single object to simplify
 * conditional logic and submit to the endpoint.
 *
 * @param data formGroup data
 * @returns
 */
export function flattenRawData(data: KeyValue, pageKeys: string[]): KeyValue {
  let flattened: KeyValue = data;
  Object.keys(data).forEach((key: string) => {
    if (!pageKeys.includes(key)) return;
    flattened = Object.assign(flattened, flattened[key]);
    delete flattened[key];
  });

  return flattened;
}

/**
 * Deserializes and normalizes the field conditions/states
 * that are returned from the webform_rest endpoint
 * @param conditions
 */
export function deserializeConditional(conditions: { [key: string]: any }): any {
  const conditionals: any[] = [];
  for (const key in conditions) {
    const condition = conditions[key];
    const conditional: Conditional = {
      state: CONDITIONAL_STATES[key],
      // default operator
      operator: 'and',
      // normalize conditions format
      conditions: Array.isArray(condition) ? condition : [],
    };

    // normalize conditions that are returned in an object
    if (condition instanceof Object && !Array.isArray(condition)) {
      Object.keys(condition).forEach((key: string) => {
        const normalized: any = {};
        normalized[key] = condition[key];
        conditional.conditions.push(normalized);
      });
    }

    // Set correct operator if the state array contains the operator
    if ((conditional.conditions as any).indexOf('xor') > -1) conditional.operator = 'or';
    if ((conditional.conditions as any).indexOf('or') > -1) conditional.operator = 'or';

    // Remove the state operator from the conditions
    if (Array.isArray(conditional.conditions)) {
      conditional.conditions = conditional.conditions.filter(
        (t: any) => t !== 'or' && t !== 'xor'
      );
    }

    // Normalize conditions
    const normalized: any = [];
    conditional.conditions.forEach((c: any) => {
      // Field ID based on the selector
      const selector = Object.keys(c)[0];
      // Type of the conditional check
      let type = Object.keys(c[selector])[0];
      // Value to which compare the conditional
      let comparator = c[selector][type];

      if (comparator instanceof Object && !Array.isArray(comparator)) {
        type = Object.keys(comparator)[0];
        comparator = comparator[type];
      }

      // set up evaluation for runtime validation
      if (!!type && !!comparator) {
        const evaluation = CONDITIONAL_TRIGGERS[type].replace('{comparator}', comparator);
        normalized.push({
          field: selector.replace(':input[name="', '').replace('"]', ''),
          evaluation,
        });
      }
    });

    conditional.conditions = normalized;
    conditionals.push(conditional);
  }
  return conditionals;
}

export function downloadPdf(file: any, name: string, isBase64: boolean = true) {
  let blob: Blob = file;

  if (isBase64) {
    const byteCharacters = atob(file.replace('data:application/pdf;base64,', ''));
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    blob = new Blob([byteArray], { type: 'application/pdf' });
  }

  saveAs(blob, name);

  (window as any)?.utag.link({
    tealium_event: 'document_download',
    download_filetype: 'pdf',
    download_title: name,
    // download_url: file
  });
}

export function formatNumber(n: number, format: string): string {
  try {
    numeral.register('locale', 'custom', {
      delimiters: {
        thousands: "'",
        decimal: '.',
      },
      abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't',
      },
      ordinal: function () {
        return '.';
      },
      currency: {
        symbol: 'CHF ',
      },
    });
    numeral.locale('custom');
  } catch (e) {}
  return numeral(n).format(format);
}
